import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import GoldDivider from "@/components/GoldDivider";
import Reveal from "@/components/Reveal";

const headlines = [
"Where Capital Meets\nReal Estate at Scale",
"Capital.\nControl.\nReal Estate."];


const capabilities = [
{
  title: "Capital Advisory & Syndication",
  desc: "Deploying institutional capital from family offices, HNIs, and investors into curated, high-conviction opportunities."
},
{
  title: "Developer & Platform Partnerships",
  desc: "Structuring joint ventures, capital stacks, and long-term development platforms alongside India's leading developers."
},
{
  title: "Real Estate Investment Banking",
  desc: "Private equity transactions, structured capital placement, and high-value deal execution across asset classes."
},
{
  title: "Portfolio Strategy & Optimization",
  desc: "Portfolio construction, yield enhancement, and exit strategy advisory for discerning investors."
},
{
  title: "Leasing, Monetisation & Asset Stabilisation",
  desc: "Enhancing asset performance through strategic leasing and income optimization."
},
{
  title: "Corporate & Industrial Advisory",
  desc: "Land acquisition, built-to-suit solutions, and corporate real estate strategy for expanding organizations."
}];


const whyPoints = [
"Access to off-market opportunities",
"Deep relationships across capital and developer networks",
"Pan-India execution capability",
"Strategy-first, not transaction-first",
"Discreet handling of high-value transactions",
"Alignment-driven deal structuring"];


const clients = ["AURUM DEVELOPERS", "K RAHEJA CORP",
"SRIJAN GROUP", "SIKKA GROUP", "INDIAN CITY PROPERTIES (ICP)",
"PEERLESS GROUP", "CHAUDHARY GROUP", "CHALET HOTELS",
"SAMHI HOTELS", "EDELWEISS ALTERNATIVES", "MOTILAL OSWAL ALTERNATES", "ATMOSPHERE HOTELS AND RESORTS", "PANCHSHIL REALTY",
"INDOSPACE", "INDIAN BUILDERS CORP (IBC)\nBANGALORE"];


const leadership = [
{
  name: "Piyush Agarwal",
  role: "Founder & Director of Business Development",
  bio: "Chartered Accountant and capital markets strategist. Established networks spanning global industrialists, institutional investors, and India's leading developers.",
  initials: "PA",
  image: "https://customer-assets.emergentagent.com/job_ksg-capital-partners/artifacts/lr2f50v5_Piyush%20Agarwal.jpeg"
},
{
  name: "Jatin Mundhra",
  role: "Director, Hospitality",
  bio: "Specialist in premium hospitality acquisitions and dispositions across India, Dubai, and the Maldives. A unique strategic lens on high-stakes asset transactions.",
  initials: "JM",
  image: "https://customer-assets.emergentagent.com/job_ksg-capital-partners/artifacts/lctnnsn3_4508beb4-90ff-4508-bda7-d60494894e41.jpg"
},
{
  name: "Akshat Dubey",
  role: "Director of Leasing & Land Acquisitions",
  bio: "Strategic leasing and capital markets specialist with deep expertise across commercial and retail leasing transactions, as well as Land Acquisitions and Joint Venture Structuring.",
  initials: "AD",
  image: "https://customer-assets.emergentagent.com/job_ksg-capital-partners/artifacts/anir7ckc_Akshat%20Dubey.jpeg"
}];


// ─── Hero ───
function HeroSection() {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => {setIdx((i) => (i + 1) % headlines.length);setFade(true);}, 700);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden noise-overlay"
      style={{
        backgroundImage:
        "url(https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?q=80&w=2244)",
        backgroundSize: "cover",
        backgroundPosition: "center 60%"
      }}>

      <div className="absolute inset-0 bg-gradient-to-b from-[#040A12] via-[#040A12]/80 to-[#040A12]" />
      <div className="absolute inset-0 bg-[#0B1F3A]/30" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-12 text-center">
        <div className="mb-8">
          <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-[#C6A75E]/80">
            Real Estate Advisory &nbsp;·&nbsp; Capital Markets &nbsp;·&nbsp; India
          </span>
        </div>

        <h1
          data-testid="hero-headline"
          className={`font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white tracking-tight leading-tight mb-8 whitespace-pre-line transition-opacity duration-700 ${fade ? "opacity-100" : "opacity-0"}`}>

          {headlines[idx]}
        </h1>

        <div className="h-px w-16 mx-auto mb-8 gold-pulse" style={{ background: "linear-gradient(to right, transparent, #C6A75E, transparent)" }} />

        <p className="font-sans text-base sm:text-lg font-light text-slate-400 max-w-2xl mx-auto mb-14 leading-relaxed">
          Strategic advisory and transaction execution for developers, family offices, and institutional investors across India.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
          <Link
            to="/contact"
            data-testid="hero-cta-primary"
            className="px-10 py-4 border border-[#C6A75E]/60 text-[#C6A75E] font-sans text-[10px] uppercase tracking-[0.25em] hover:bg-[#C6A75E]/8 hover:border-[#C6A75E] transition-all duration-600">

            Initiate a Conversation
          </Link>
          <Link
            to="/services"
            data-testid="hero-cta-secondary"
            className="flex items-center gap-2 text-white/50 font-sans text-[10px] uppercase tracking-[0.25em] hover:text-[#C6A75E] transition-colors duration-600 group">

            Access Opportunities
            <ArrowRight size={12} strokeWidth={1} className="group-hover:translate-x-1 transition-transform duration-400" />
          </Link>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="font-sans text-[9px] uppercase tracking-[0.3em] text-[#C6A75E]/40">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-[#C6A75E]/40 to-transparent scroll-indicator" />
      </div>
    </section>);

}

// ─── Positioning ───
function PositioningSection() {
  return (
    <section data-testid="positioning-section" className="py-32 px-6 sm:px-12 lg:px-24 max-w-5xl mx-auto">
      <Reveal>
        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-8">
          Who We Are
        </span>
      </Reveal>
      <Reveal delay={150}>
        <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-white leading-relaxed mb-8">
          KSG Realty operates at the intersection of capital and real estate execution.
        </p>
      </Reveal>
      <Reveal delay={300}>
        <div className="h-px w-12 bg-[#C6A75E]/50 mb-8" />
      </Reveal>
      <Reveal delay={400}>
        <p className="font-sans text-base font-light text-slate-400 leading-loose max-w-3xl">
          We advise developers, family offices, and institutional investors on complex, high-value transactions across commercial, land, hospitality, and mixed-use assets. Our role is not transactional — we act as long-term partners in structuring, aligning, and deploying capital with discretion, precision, and strategic clarity.
        </p>
      </Reveal>
    </section>);

}

// ─── Capabilities ───
function CapabilitiesSection() {
  return (
    <section data-testid="capabilities-section" className="py-24 px-6 sm:px-12 lg:px-24 max-w-7xl mx-auto">
      <Reveal>
        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-4">
          Core Capabilities
        </span>
      </Reveal>
      <Reveal delay={100}>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-16">
          What We Do
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C6A75E]/8">
        {capabilities.map((cap, i) =>
        <Reveal key={cap.title} delay={i * 80}>
            <div
            data-testid={`capability-card-${i}`}
            className="bg-[#081323] hover:bg-[#0B1F3A]/60 border border-transparent hover:border-[#C6A75E]/20 transition-all duration-700 p-8 h-full group">

              <div className="w-8 h-px bg-[#C6A75E]/50 mb-6 group-hover:w-12 transition-all duration-700" />
              <h3 className="font-serif text-lg font-light text-[#C6A75E] mb-4 leading-snug">{cap.title}</h3>
              <p className="font-sans text-sm font-light text-slate-400 leading-relaxed">{cap.desc}</p>
            </div>
          </Reveal>
        )}
      </div>
    </section>);

}

// ─── Select Relationships ───
function ClientsSection() {
  return (
    <section data-testid="clients-section" className="py-24 px-6 sm:px-12 lg:px-24 max-w-7xl mx-auto">
      <Reveal>
        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-4">
          Select Relationships
        </span>
      </Reveal>
      <Reveal delay={100}>
        <h2 className="font-serif text-3xl sm:text-4xl font-light text-white tracking-tight mb-4">
          Those We Work With
        </h2>
      </Reveal>
      <Reveal delay={200}>
        <p className="font-sans text-sm font-light text-slate-500 mb-16 max-w-xl">
          Our network spans leading developers, institutional investors, and global capital partners.
        </p>
      </Reveal>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {clients.map((name, i) =>
        <Reveal key={name} delay={i * 40}>
            <div
            data-testid={`client-${i}`}
            className="group flex items-center justify-center px-4 py-4 border border-[#C6A75E]/18 bg-[#081323]/70 hover:border-[#C6A75E]/55 hover:bg-[#0B1F3A]/60 transition-all duration-600 min-h-[56px]">

              <span className="font-sans text-[10px] uppercase tracking-[0.14em] text-slate-400 group-hover:text-[#C6A75E] transition-colors duration-600 text-center leading-relaxed">
                {name}
              </span>
            </div>
          </Reveal>
        )}
      </div>
    </section>);

}

// ─── Why KSG ───
function WhyKSGSection() {
  return (
    <section data-testid="why-ksg-section" className="py-24 px-6 sm:px-12 lg:px-24 bg-[#040A12]">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal>
            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-4">Why KSG Realty</span>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-6">
              A Distinct Advantage
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="font-sans text-sm font-light text-slate-500 leading-relaxed">We operate where most firms cannot. Built on trust, discretion, and years of relationships.

            </p>
          </Reveal>
        </div>
        <div className="space-y-0">
          {whyPoints.map((point, i) =>
          <Reveal key={point} delay={i * 80}>
              <div
              data-testid={`why-point-${i}`}
              className="flex items-start gap-5 py-5 border-b border-[#C6A75E]/8 group hover:border-[#C6A75E]/25 transition-colors duration-500">

                <div className="w-1.5 h-1.5 rounded-full bg-[#C6A75E]/50 mt-2 flex-shrink-0 group-hover:bg-[#C6A75E] transition-colors duration-500" />
                <span className="font-sans text-sm font-light text-slate-400 group-hover:text-white/80 transition-colors duration-500">
                  {point}
                </span>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>);

}



// ─── Philosophy ───
function PhilosophySection() {
  return (
    <section
      data-testid="philosophy-section"
      className="py-32 px-6 sm:px-12 lg:px-24 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #040A12 0%, #081323 50%, #040A12 100%)" }}>

      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: "url(https://images.unsplash.com/photo-1647101734210-ff5d79813069?q=40&w=800)",
        backgroundSize: "cover",
        backgroundPosition: "center"
      }} />
      <div className="relative max-w-4xl mx-auto text-center">
        <Reveal>
          <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-10">
            Our Philosophy
          </span>
        </Reveal>
        <Reveal delay={150}>
          <p className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-white leading-relaxed mb-8 italic">
            "Scale without discipline destroys value."
          </p>
        </Reveal>
        <Reveal delay={300}>
          <div className="h-px w-10 bg-[#C6A75E]/50 mx-auto mb-8" />
        </Reveal>
        <Reveal delay={400}>
          <p className="font-sans text-sm font-light text-slate-400 leading-loose max-w-2xl mx-auto">
            Every transaction is approached as a long-term alignment of capital, vision, and execution. We are selective in the opportunities we engage with and the partners we work alongside.
          </p>
        </Reveal>
      </div>
    </section>);

}

// ─── Leadership ───
function LeadershipSection() {
  return (
    <section data-testid="leadership-section" className="py-24 px-6 sm:px-12 lg:px-24 max-w-7xl mx-auto">
      <Reveal>
        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-4">Leadership</span>
      </Reveal>
      <Reveal delay={100}>
        <h2 className="font-serif text-3xl sm:text-4xl font-light text-white tracking-tight mb-16">
          The Team
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {leadership.map((person, i) =>
        <Reveal key={person.name} delay={i * 120}>
            <div
            data-testid={`leader-${i}`}
            className="bg-[#081323] border border-[#C6A75E]/10 hover:border-[#C6A75E]/30 transition-all duration-700 p-8 group">

      {/* Avatar */}
              <div className="w-full aspect-square border border-[#C6A75E]/25 group-hover:border-[#C6A75E]/50 transition-colors duration-700 mb-6 bg-[#0B1F3A]/40 overflow-hidden flex items-center justify-center">
                {person.image ?
              <img
                src={person.image}
                alt={person.name}
                className="w-full h-full object-cover object-top" /> :


              <span className="font-serif text-5xl text-[#C6A75E]/30 group-hover:text-[#C6A75E]/60 transition-colors duration-700">
                    {person.initials}
                  </span>
              }
              </div>
              <h3 className="font-serif text-lg font-light text-white mb-1">{person.name}</h3>
              <p className="uppercase font-sans text-[10px] tracking-[0.15em] mb-5 !text-[#C6A75E]">{person.role}</p>
              <div className="h-px w-8 bg-[#C6A75E]/30 mb-5" />
              <p className="font-sans text-sm font-light text-slate-500 leading-relaxed">{person.bio}</p>
            </div>
          </Reveal>
        )}
      </div>
    </section>);

}

// ─── Final CTA ───
function CTASection() {
  return (
    <section
      data-testid="cta-section"
      className="py-32 px-6 sm:px-12 lg:px-24 bg-[#081323] border-t border-[#C6A75E]/10">

      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-6">
            Get in Touch
          </span>
        </Reveal>
        <Reveal delay={150}>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-6">
            Let's Structure Your Next Opportunity
          </h2>
        </Reveal>
        <Reveal delay={300}>
          <p className="font-sans text-sm font-light text-slate-400 mb-12 leading-relaxed">
            For developers, investors, and institutions operating at scale.
          </p>
        </Reveal>
        <Reveal delay={400}>
          <div className="flex justify-center">
            <Link
              to="/contact"
              data-testid="cta-start-conversation"
              className="px-10 py-4 border border-[#C6A75E]/60 text-[#C6A75E] font-sans text-[10px] uppercase tracking-[0.25em] hover:bg-[#C6A75E]/8 hover:border-[#C6A75E] transition-all duration-600">
              Start a Conversation
            </Link>
          </div>
        </Reveal>
      </div>
    </section>);

}

// ─── Home Page ───
export default function Home() {
  return (
    <div className="bg-[#040A12]" data-testid="home-page">
      <HeroSection />
      <GoldDivider />
      <PositioningSection />
      <GoldDivider />
      <CapabilitiesSection />
      <GoldDivider />
      <ClientsSection />
      <GoldDivider />
      <WhyKSGSection />
      <GoldDivider />
      <PhilosophySection />
      <GoldDivider />
      <LeadershipSection />
      <GoldDivider />
      <CTASection />
    </div>);

}